import React, { useState, createElement } from 'react';
import { Search, ChevronLeft, ChevronRight, Download } from 'lucide-react';
interface Column {
  id: string;
  header: string;
  cell: (row: any) => React.ReactNode;
  sortable?: boolean;
}
interface DataTableProps {
  data: any[];
  columns: Column[];
  title?: string;
  actions?: React.ReactNode;
  searchPlaceholder?: string;
  pagination?: boolean;
  itemsPerPage?: number;
  exportable?: boolean;
  onRowClick?: (row: any) => void;
}
export const DataTable: React.FC<DataTableProps> = ({
  data,
  columns,
  title,
  actions,
  searchPlaceholder = 'Search...',
  pagination = true,
  itemsPerPage = 10,
  exportable = true,
  onRowClick
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  // Search functionality
  const filteredData = data.filter(row => {
    return Object.values(row).some(value => String(value).toLowerCase().includes(searchTerm.toLowerCase()));
  });
  // Sorting functionality
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortColumn) return 0;
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    const aString = String(aValue).toLowerCase();
    const bString = String(bValue).toLowerCase();
    return sortDirection === 'asc' ? aString.localeCompare(bString) : bString.localeCompare(aString);
  });
  // Pagination functionality
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = pagination ? sortedData.slice(startIndex, startIndex + itemsPerPage) : sortedData;
  const handleSort = (columnId: string) => {
    if (sortColumn === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };
  const handleExport = () => {
    // Simple CSV export
    const headers = columns.map(col => col.header).join(',');
    const rows = filteredData.map(row => columns.map(col => {
      const cellValue = row[col.id];
      // Handle values with commas
      return typeof cellValue === 'string' && cellValue.includes(',') ? `"${cellValue}"` : cellValue;
    }).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'data'}_export.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-11242">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex flex-wrap items-center justify-between gap-4" data-id="element-11243">
        {title && <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-11244">{title}</h3>}
        <div className="flex flex-1 items-center space-x-4 ml-auto" data-id="element-11245">
          <div className="relative flex-1 max-w-xs" data-id="element-11246">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-11247">
              <Search size={16} className="text-gray-400 dark:text-gray-500" data-id="element-11248" />
            </div>
            <input type="text" className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 block w-full pl-10 p-2" placeholder={searchPlaceholder} value={searchTerm} onChange={e => {
            setSearchTerm(e.target.value);
            setCurrentPage(1); // Reset to first page on search
          }} data-id="element-11249" />
          </div>
          {exportable && <button onClick={handleExport} className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-11250">
              <Download size={16} className="mr-2" data-id="element-11251" />
              Export
            </button>}
          {actions}
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-11252">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-11253">
          <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-11254">
            <tr data-id="element-11255">
              {columns.map(column => <th key={column.id} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => column.sortable !== false && handleSort(column.id)} data-id="element-11256">
                  <div className="flex items-center" data-id="element-11257">
                    {column.header}
                    {column.sortable !== false && sortColumn === column.id && <span className="ml-1" data-id="element-11258">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>}
                  </div>
                </th>)}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-11259">
            {paginatedData.length > 0 ? paginatedData.map((row, rowIndex) => <tr key={rowIndex} className={onRowClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : ''} onClick={() => onRowClick && onRowClick(row)} data-id="element-11260">
                  {columns.map(column => <td key={column.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200" data-id="element-11261">
                      {column.cell(row)}
                    </td>)}
                </tr>) : <tr data-id="element-11262">
                <td colSpan={columns.length} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-11263">
                  No data found
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
      {pagination && totalPages > 1 && <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-11264">
          <div className="flex-1 flex justify-between items-center" data-id="element-11265">
            <div data-id="element-11266">
              <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-11267">
                Showing <span className="font-medium" data-id="element-11268">{startIndex + 1}</span> to{' '}
                <span className="font-medium" data-id="element-11269">
                  {Math.min(startIndex + itemsPerPage, filteredData.length)}
                </span>{' '}
                of <span className="font-medium" data-id="element-11270">{filteredData.length}</span>{' '}
                results
              </p>
            </div>
            <div className="flex space-x-2" data-id="element-11271">
              <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} className={`inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-md ${currentPage === 1 ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-11272">
                <ChevronLeft size={16} data-id="element-11273" />
                Previous
              </button>
              <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} className={`inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-md ${currentPage === totalPages ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-11274">
                Next
                <ChevronRight size={16} data-id="element-11275" />
              </button>
            </div>
          </div>
        </div>}
    </div>;
};